import { useEffect, useState } from "react";
import { useSaveSharedTrip, type SaveSharedInput } from "@/lib/api/hooks";
import type { Trip } from "@/lib/api/schemas";

interface SaveSharedDialogProps {
  token: string;
  source: Trip;
  onClose: () => void;
  onSaved: (newTrip: Trip) => void;
  // Called when the server reports 409 (requester already owns the
  // source trip) so the parent can redirect instead of duplicating.
  onConflict?: (existingTripId: string) => void;
}

// Lets the recipient of a shared trip tailor the clone before saving:
// title, dates, traveller count, optional chat-history copy. A "Quick
// save" link below the primary CTA skips the form and saves as-is.
export function SaveSharedDialog({
  token,
  source,
  onClose,
  onSaved,
  onConflict,
}: SaveSharedDialogProps) {
  const [title, setTitle] = useState(source.title || "");
  const [startDate, setStartDate] = useState(source.startDate || "");
  const [endDate, setEndDate] = useState(source.endDate || "");
  const [travellers, setTravellers] = useState(source.travelersCount || 1);
  const [includeChat, setIncludeChat] = useState(false);

  const save = useSaveSharedTrip(token, { onConflict });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = (overrides?: Partial<SaveSharedInput>) => {
    const input: SaveSharedInput = {
      title: title.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      travelersCount: travellers > 0 ? travellers : undefined,
      includeChat,
      ...overrides,
    };
    save.mutate(input, {
      onSuccess: (trip) => onSaved(trip),
    });
  };

  const quickSave = () => {
    // Skip the form entirely — pure clone.
    save.mutate(
      { includeChat: false },
      {
        onSuccess: (trip) => onSaved(trip),
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-shared-title"
    >
      <div
        className="absolute inset-0 bg-deep/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-md rounded-2xl frost-panel shadow-2xl page-fade"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <h2
              id="save-shared-title"
              className="text-lg font-semibold text-ink"
            >
              Save to my trips
            </h2>
            <p className="text-xs text-ink-light mt-0.5">
              Creates your own editable copy. You can change it after
              too.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-full bg-deep/60 border border-white/20 text-ink hover:bg-deep/80"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="p-5 space-y-4"
        >
          <Field label="Trip title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={source.title}
              className="w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent placeholder:text-ink-light"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <input
                type="date"
                value={startDate ?? ""}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                value={endDate ?? ""}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </Field>
          </div>

          <Field label="Travellers">
            <input
              type="number"
              min={1}
              max={20}
              value={travellers}
              onChange={(e) => setTravellers(Number(e.target.value))}
              className="w-24 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>

          <label className="flex items-start gap-3 rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 cursor-pointer hover:bg-white/10 transition-colors">
            <input
              type="checkbox"
              checked={includeChat}
              onChange={(e) => setIncludeChat(e.target.checked)}
              className="mt-0.5 accent-accent"
            />
            <span className="text-sm text-ink">
              Include the planning conversation
              <span className="block text-xs text-ink-light mt-0.5">
                Lets you see why things were planned this way. Off by
                default — the log belongs to the original traveller.
              </span>
            </span>
          </label>

          {save.isError && (
            <p className="text-sm text-red-300 bg-red-400/10 rounded-lg px-3 py-2">
              Couldn't save. Please try again in a moment.
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={quickSave}
              disabled={save.isPending}
              className="text-xs text-ink-light hover:text-ink underline disabled:opacity-50"
            >
              Quick save (use source defaults)
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-lg bg-accent text-deep font-semibold px-5 py-2 text-sm hover:bg-accent-light disabled:opacity-60"
            >
              {save.isPending ? "Saving…" : "Save to my trips"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-ink-light/80 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
